import type { PartnerTierConfig } from '@/api/spec.ts';
import { formatCompactUsd } from '@/utils/bigUtils';
import { offsetNumberToNumber } from '@/utils/tokenNumberUtils';

export type TierOutlook = 'promotion' | 'demotion' | 'stable';

export interface PartnerTierSnapshot {
  lockedTier: number;
  liveVolumeUsd: number;
  /** `Referrals.next_partner_tier` when the API ships it; derived from the ladder otherwise. */
  projectedTier?: number;
  ladderUsd: number[];
}

export interface PartnerTierViewModel {
  projectedTier: number;
  outlook: TierOutlook;
  isAtRisk: boolean;
  volumeToKeepTier: number;
  volumeToNextTier: number;
}

/**
 * Minimum referred volume (USD) per tier; `ladderUsd[i]` is tier `i + 1`, matching
 * `Referrals.partner_tier`. Returns `[]` when the API ships no ladder (tiers are
 * partner-only) so callers hide tier UI rather than invent thresholds.
 */
export function parseTierLadderUsd(tiers: PartnerTierConfig[] | undefined, collateralDecimals: number): number[] {
  if (!tiers?.length) return [];
  const parsed = tiers.map((t) => offsetNumberToNumber(Number(t.min_volume), collateralDecimals));
  // Reject a ladder we can't reason about: the backend guarantees ascending, non-negative thresholds.
  const isSane = parsed.every((v, i) => Number.isFinite(v) && v >= 0 && (i === 0 || v >= (parsed[i - 1] ?? 0)));
  return isSane ? parsed : [];
}

/**
 * Highest tier whose minimum is met. Mirrors the backend `predictPartnerTier`:
 * a partner never falls below tier 1, and 0 means no ladder is configured.
 */
export function projectedTierFromVolume(volumeUsd: number, ladderUsd: number[]): number {
  if (!ladderUsd.length) return 0;
  const v = Math.max(0, volumeUsd);
  let tier = 1;
  for (let i = 0; i < ladderUsd.length; i++) {
    const min = ladderUsd[i];
    if (min == null) break;
    if (v >= min) tier = i + 1;
  }
  return tier;
}

export function tierMinVolume(tier: number, ladderUsd: number[]): number {
  if (tier <= 0) return 0;
  return ladderUsd[tier - 1] ?? 0;
}

/**
 * API tiers are 1-based (`partner_tier` 1 = first ladder entry, 0 = not a partner) but partners
 * see the program numbered from 0. Offset at render time only; the math above stays API-numbered.
 */
export function toDisplayTier(apiTier: number): number {
  return apiTier - 1;
}

/**
 * Volume band earning a tier: a floor once the tier starts above $0, a range from $0 for the entry
 * tier. `floorPrefix` carries the per-surface comparator (">", "≥ ", or none).
 */
export function tierVolumeBand(minUsd: number, nextMinUsd: number | undefined, floorPrefix = ''): string {
  if (minUsd > 0) return `${floorPrefix}${formatCompactUsd(minUsd)}`;
  if (nextMinUsd == null) return formatCompactUsd(0);
  // The band carries one currency symbol: "$0–25M".
  return `${formatCompactUsd(0)}–${formatCompactUsd(nextMinUsd).replace('$', '')}`;
}

/** Fill of the track segment for `tier` (1-based); the top tier is open-ended, so it fills on arrival. */
export function segmentFillRatio(tier: number, volumeUsd: number, ladderUsd: number[]): number {
  const volume = Math.max(0, volumeUsd);
  const lo = tierMinVolume(tier, ladderUsd);
  const hi = tier < ladderUsd.length ? (ladderUsd[tier] ?? lo) : Number.POSITIVE_INFINITY;

  // Top tier has no upper bound to progress towards: reaching its floor fills it.
  if (!Number.isFinite(hi)) return volume >= lo ? 1 : 0;
  if (volume >= hi) return 1;
  if (volume <= lo) return 0;
  return (volume - lo) / Math.max(hi - lo, 1);
}

export function buildPartnerTierViewModel(input: PartnerTierSnapshot): PartnerTierViewModel {
  const { lockedTier, liveVolumeUsd, ladderUsd } = input;
  // The API predicts the next tier from the same live volume; fall back to the local
  // ladder walk when it's absent so the tracker still moves.
  const projectedTier = input.projectedTier ?? projectedTierFromVolume(liveVolumeUsd, ladderUsd);
  const lockedMin = tierMinVolume(lockedTier, ladderUsd);
  const nextMin = tierMinVolume(lockedTier + 1, ladderUsd);

  const isAtRisk = lockedTier > 0 && liveVolumeUsd < lockedMin;
  const outlook: TierOutlook = projectedTier > lockedTier ? 'promotion' : projectedTier < lockedTier ? 'demotion' : 'stable';

  return {
    projectedTier,
    outlook,
    isAtRisk,
    volumeToKeepTier: isAtRisk ? Math.max(0, lockedMin - liveVolumeUsd) : 0,
    volumeToNextTier:
      lockedTier > 0 && lockedTier < ladderUsd.length && liveVolumeUsd < nextMin ? Math.max(0, nextMin - liveVolumeUsd) : 0,
  };
}

/**
 * Effective share for a tier: the public base share scaled by the tier's boost
 * multiplier (both bps, 10000 = 100%), matching how the backend applies boosts.
 */
export function boostedShareFraction(baseShareBps: number | undefined, boostBps: number | undefined): number | undefined {
  if (baseShareBps == null || boostBps == null) return undefined;
  return (baseShareBps / 10_000) * (boostBps / 10_000);
}
